import type { ICartItem } from '@/api/types/cart';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { genPrice } from '@/utils/price';
import { useCallback } from 'react';

// 扩展CartItem类型以支持更多字段
interface CartItem extends ICartItem {
  product: ICartItem['product'] & {
    shop_sku?: string;
    spu?: string;
    image?: {
      celebrity?: string[];
      material?: string[];
      product?: string[];
    };
  };
}

/**
 * 购物车第三方埋点Hook
 * 统一管理Criteo、Uetq(Bing)、Fbq(Facebook)、Axon、Emarsys等埋点
 */
export function useCartThirdPartyTracking() {
  const { trackEvent } = useReporting();
  const { currency, userInfo } = useStore((state) => state);

  /**
   * Criteo埋点：查看购物车
   * 对应Vue版本中shopping-cart.vue的viewBasket事件
   */
  const trackCriteoViewBasket = useCallback(
    (Items: CartItem[]) => {
      try {
        if (!Items || Items.length === 0) {
          logger.warn('没有商品，跳过Criteo viewBasket埋点');
          return;
        }

        // 构建Criteo商品数据数组
        const criteoItems = Items.map((item) => {
          const itemPrice = genPrice(item.product?.price_info, currency?.name)?.price || '0';
          return {
            id: item.product_id,
            price: itemPrice,
            quantity: item.qty || 1,
          };
        });

        // 发送viewBasket事件
        trackEvent(
          {
            eventName: 'viewBasket',
            properties: {
              item: criteoItems,
            },
          },
          [ProviderType.CRITEO],
        );

        logger.log('🔍 Criteo埋点 - viewBasket:', { Items: Items.length, criteoItems });
      } catch (error) {
        logger.error('Criteo viewBasket tracking error:', error);
      }
    },
    [trackEvent, currency],
  );

  /**
   * Uetq (Bing)埋点：购物车页面浏览
   * 对应Vue版本中shopping-cart.vue的bing埋点
   */
  const trackUetqCart = useCallback(
    (canBuyList: CartItem[][]) => {
      try {
        if (!canBuyList || canBuyList.length === 0) {
          logger.warn('没有可购买商品，跳过Uetq埋点');
          return;
        }

        // 构建Bing商品ID数组
        const bingArray: number[] = [];
        canBuyList.forEach((item) => {
          bingArray.push(...item.map((cartItem) => cartItem.product_id));
        });

        // 发送到Bing Provider
        trackEvent(
          {
            eventName: '',
            properties: {
              ecomm_prodid: bingArray,
              ecomm_pagetype: 'cart',
            },
          },
          [ProviderType.BING],
        );
        // TODO: 临时放这里吧，后续这些页面的埋点都需要统一集中处理一次，现在散落各地
        const rtbArray: string[] = [];
        canBuyList.forEach((item) => {
          rtbArray.push(...item.map((cartItem) => cartItem.product.shop_sku));
        });
        trackEvent(
          {
            eventName: 'basketstatus',
            properties: {
              offerIds: rtbArray,
            },
          },
          [ProviderType.RTB_HOUSE],
        );

        logger.log('🔍 Uetq埋点 - 购物车浏览:', { productIds: bingArray });
      } catch (error) {
        logger.error('Uetq cart tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * Facebook埋点：添加到心愿单
   * 对应Vue版本中CartItem.vue的fbqSend AddToWishlist事件
   */
  const trackFacebookAddToWishlist = useCallback(
    (cartItem: CartItem) => {
      try {
        if (!cartItem.product?.shop_sku) {
          logger.warn('缺少shop_sku，跳过Facebook埋点');
          return;
        }

        const fbqData = {
          content_type: 'product',
          content_ids: cartItem.product.shop_sku,
          is_new_customer: userInfo?.first_order_at ? 2 : 1,
          first_order_at: userInfo?.first_order_at || null,
        };

        // 使用Facebook Provider发送埋点
        trackEvent(
          {
            eventName: 'AddToWishlist',
            properties: fbqData,
          },
          [ProviderType.FACEBOOK],
        );

        logger.log('🔍 Facebook埋点 - AddToWishlist:', {
          cartItem: cartItem.product.shop_sku,
          fbqData,
        });
      } catch (error) {
        logger.error('Facebook AddToWishlist tracking error:', error);
      }
    },
    [userInfo],
  );

  /**
   * Axon埋点：开始结账
   * 对应Vue版本中shopping-cart.vue的begin_checkout事件
   */
  const trackAxonBeginCheckout = useCallback(
    (selectedItems: CartItem[], totalPrice: string) => {
      try {
        if (!selectedItems || selectedItems.length === 0) {
          logger.warn('没有选中商品，跳过Axon埋点');
          return;
        }

        // 构建Axon商品数据
        const axonItems = selectedItems.map((item) => {
          const sellPrice =
            genPrice(item.product?.price_info, env.NEXT_PUBLIC_DEFAULT_CURRENCY || currency?.name)
              ?.sell_price || '0';
          return {
            item_id: item.product?.spu || '',
            item_variant_id: item.product?.shop_sku || '',
            image_url:
              item.product?.image?.celebrity?.[0] ||
              item.product?.image?.material?.[0] ||
              item.product?.image?.product?.[0] ||
              '',
            item_name: item.product?.short_name || '',
            price: parseFloat(sellPrice),
            quantity: item.qty || 1,
          };
        });

        // 发送Axon埋点 (使用APPLOVIN Provider)
        trackEvent(
          {
            eventName: 'begin_checkout',
            properties: {
              currency: env.NEXT_PUBLIC_DEFAULT_CURRENCY || currency?.name,
              items: axonItems,
              value: parseFloat(totalPrice.replace(/[^\d.]/g, '')),
            },
          },
          [ProviderType.APPLOVIN],
        );

        logger.log('🔍 Axon埋点 - begin_checkout:', {
          itemsCount: selectedItems.length,
          totalPrice,
          axonItems,
        });
      } catch (error) {
        logger.error('Axon begin_checkout tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * Emarsys埋点：购物车追踪
   * 对应Vue版本中shopping-cart.vue的sendEmarsys函数
   */
  const trackEmarsysCart = useCallback(
    (cartData: any) => {
      try {
        if (!cartData?.tmp?.carts || !Array.isArray(cartData.tmp.carts)) {
          logger.warn('无效的购物车数据，跳过Emarsys埋点');
          return;
        }

        // 构建Emarsys购物车数据数组
        const emarsysItems = cartData.tmp.carts.map((item: any) => ({
          item: item.shop_sku, // 商品SKU
          quantity: item.qty || 1, // 数量
          price:
            parseFloat(item.base_product_payable || '0') +
            parseFloat(item.base_product_rule_discounts_price || '0'), // 以美元为基础的售价
        }));

        // 发送到Emarsys Provider
        trackEvent(
          {
            eventName: 'cart',
            properties: emarsysItems,
          },
          [ProviderType.EMARSYS],
        );

        logger.log('🔍 Emarsys埋点 - cart:', { emarsysItems });
      } catch (error) {
        logger.error('Emarsys cart tracking error:', error);
      }
    },
    [trackEvent],
  );

  return {
    // Criteo
    trackCriteoViewBasket,
    // Uetq (Bing)
    trackUetqCart,
    // Facebook
    trackFacebookAddToWishlist,
    // Axon
    trackAxonBeginCheckout,
    // Emarsys
    trackEmarsysCart,
  };
}
